import React from "react";

import ErrorIcon from "@mui/icons-material/ErrorOutline";
import { Stack } from "@mui/material";
import Decimal from "decimal.js";
import { useParams } from "react-router-dom";

import useClientDashboard from "@fartherfinance/frontend/api/Dashboard/hooks/useClientDashboard";
import useClientProfile from "@fartherfinance/frontend/api/Entity/hooks/useClientProfile";
import useDirectClientPortfolioPlanUsage from "@fartherfinance/frontend/api/Investment/hooks/useDirectClientPortfolioPlanUsage";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import usePortfolioPlanParents from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanParents";
import useSleeveClientPortfolioPlanUsage from "@fartherfinance/frontend/api/Investment/hooks/useSleeveClientPortfolioPlanUsage";
import {
  ClientId,
  PaginationMetadata,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import { UNKNOWN_USER } from "../../../common";
import BaseDrawer from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioPlanTab/ModelDrawer/BaseDrawer/BaseDrawer";
import {
  TableContainerV2,
  TableHeaderV2,
} from "@src/multiCustodian/components/Client/Portfolio/shared/shared";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { BorderedRow } from "@src/sharedComponents/List/BorderedList";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { FartherTablePagination } from "@src/sharedComponents/Table/Components";
import Text from "@src/sharedComponents/Typography/Text";

const onPageChange = (_page: number) => {
  //TODO: Pagination is not supported in the hooks
};

const ModelDrawerUsageV2: React.FC = () => {
  const { portfolioPlanId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
  }>();

  const auth = useAdvisorRequestAuth();

  const portfolioPlanUsage = useDirectClientPortfolioPlanUsage(
    portfolioPlanId,
    auth
  );

  const sleeveUsage = useSleeveClientPortfolioPlanUsage(portfolioPlanId, auth);

  const parentsUsage = usePortfolioPlanParents(portfolioPlanId, auth);

  return (
    <BaseDrawer>
      <Stack spacing={2}>
        <ClientPortfolioPlanUsageTable {...portfolioPlanUsage} />
        <SleeveClientPortfolioPlanUsageTable {...sleeveUsage} />
        <PortfolioPlanParentsTable {...parentsUsage} />
      </Stack>
    </BaseDrawer>
  );
};

export default ModelDrawerUsageV2;

type ClientPortfolioPlanUsageTableProps =
  | { data: undefined; hasError: false; isLoading: true }
  | { data: undefined; hasError: true; isLoading: false }
  | {
      data: {
        pagination: PaginationMetadata;
        data: { clientId: ClientId }[];
      };
      hasError: false;
      isLoading: false;
    };

const ClientPortfolioPlanUsageTable: React.FC<
  ClientPortfolioPlanUsageTableProps
> = ({ data, hasError, isLoading }) => {
  if (hasError) {
    return <ErrorState title="Clients using the model" />;
  }

  if (isLoading) {
    return <LoadingState title="Clients using the model" />;
  }

  if (data?.data.length === 0) {
    return <EmptyState title="Clients using the model" />;
  }

  const pagination = data?.pagination;

  return (
    <TableContainerV2>
      <TableHeaderV2 leftTitle="Clients using the model" rightTitle="Export" />

      <BorderedRow left={"Client"} right={"Advisor"} />
      {data.data.map((portfolioPlan, i) => (
        <ClientRow
          key={i}
          clientId={portfolioPlan.clientId}
          lastItem={i === data.data.length - 1}
        />
      ))}

      <FartherTablePagination
        pageSize={pagination.pageSize}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        onPageChange={(page) => onPageChange(page + 1)}
        isPrevButtonDisabled={pagination?.currentPage <= 1}
        isNextButtonDisabled={pagination.currentPage + 1 > pagination.pageCount}
      />
    </TableContainerV2>
  );
};

type SleeveClientPortfolioPlanUsageTableProps =
  | { data: undefined; hasError: false; isLoading: true }
  | { data: undefined; hasError: true; isLoading: false }
  | {
      data: {
        pagination: PaginationMetadata;
        data: {
          clientId: ClientId;
          portfolioPlanId: PortfolioPlanId;
          immediateWeight: Decimal | null;
        }[];
      };
      hasError: false;
      isLoading: false;
    };

const SleeveClientPortfolioPlanUsageTable: React.FC<
  SleeveClientPortfolioPlanUsageTableProps
> = ({ data, isLoading, hasError }) => {
  if (hasError) {
    return <ErrorState title="Clients using this as a sleeve" />;
  }

  if (isLoading) {
    return <LoadingState title="Clients using this as a sleeve" />;
  }

  if (data?.data.length === 0) {
    return <EmptyState title="Clients using this as a sleeve" />;
  }

  const pagination = data?.pagination;

  return (
    <TableContainerV2>
      <TableHeaderV2
        leftTitle="Clients using this as a sleeve"
        rightTitle="Export"
      />
      <BorderedRow left={"Client"} middle="Model" right={"Weight"} />

      {data.data.map((portfolioPlan, i) => (
        <ClientSleeveRow
          key={i}
          clientId={portfolioPlan.clientId}
          portfolioPlanId={portfolioPlan.portfolioPlanId}
          lastItem={i === data.data.length - 1}
          immediateWeight={portfolioPlan.immediateWeight}
        />
      ))}

      <FartherTablePagination
        pageSize={pagination.pageSize}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        onPageChange={(page) => onPageChange(page + 1)}
        isPrevButtonDisabled={pagination?.currentPage <= 1}
        isNextButtonDisabled={pagination.currentPage + 1 > pagination.pageCount}
      />
    </TableContainerV2>
  );
};

type PortfolioPlanParentsTableProps =
  | { data: undefined; hasError: false; isLoading: true }
  | { data: undefined; hasError: true; isLoading: false }
  | {
      data: {
        pagination: PaginationMetadata;
        data: { parent: PortfolioPlanId; effectiveWeight: Decimal | null }[];
      };
      hasError: boolean;
      isLoading: boolean;
    };

const PortfolioPlanParentsTable: React.FC<PortfolioPlanParentsTableProps> = ({
  data,
  hasError,
  isLoading,
}) => {
  if (hasError) {
    return <ErrorState title="Other models with allocations to this model" />;
  }

  if (isLoading) {
    return <LoadingState title="Other models with allocations to this model" />;
  }

  if (data?.data.length === 0) {
    return <EmptyState title="Other models with allocations to this model" />;
  }

  const pagination = data?.pagination;
  return (
    <TableContainerV2>
      <TableHeaderV2
        leftTitle="Other models with allocations to this model"
        rightTitle="Export"
      />

      <BorderedRow left={"Model"} right={"Weight"} />

      {data.data.map((portfolioPlan, i) => (
        <PortfolioRow
          key={i}
          portfolioPlanId={portfolioPlan.parent}
          effectiveWeight={portfolioPlan.effectiveWeight}
          lastRow={i === data.data.length - 1}
        />
      ))}

      <FartherTablePagination
        pageSize={pagination.pageSize}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        onPageChange={(page) => onPageChange(page + 1)}
        isPrevButtonDisabled={pagination?.currentPage <= 1}
        isNextButtonDisabled={pagination.currentPage + 1 > pagination.pageCount}
      />
    </TableContainerV2>
  );
};

type ClientRowProps = {
  clientId: ClientId;
  lastItem: boolean;
};

const ClientRow: React.FC<ClientRowProps> = ({ clientId, lastItem }) => {
  const auth = useAdvisorRequestAuth();
  const newAuth =
    auth !== null
      ? {
          clientId,
          ...auth,
        }
      : null;

  const { data } = useClientProfile(clientId, newAuth);
  const { data: dashBoardData } = useClientDashboard(clientId, newAuth);

  if (data === undefined || dashBoardData === undefined) {
    return (
      <BorderedRow
        left={<Skeleton width={120} />}
        right={<Skeleton width={120} />}
        removeBorderBottom
      />
    );
  }

  return (
    <BorderedRow
      left={`${data.investorProfile.personalDetails.name.first} ${data.investorProfile.personalDetails.name.last}`}
      right={`${dashBoardData.advisor.name.first} ${dashBoardData.advisor.name.last}`}
      removeBorderBottom={lastItem}
    />
  );
};

type ClientRowSleeveProps = ClientRowProps & {
  portfolioPlanId: PortfolioPlanId;
  immediateWeight: Decimal | null;
};

const ClientSleeveRow: React.FC<ClientRowSleeveProps> = ({
  clientId,
  lastItem,
  immediateWeight,
  portfolioPlanId,
}) => {
  const auth = useAdvisorRequestAuth();

  const clientAuth = auth !== null ? { ...auth, clientId: clientId } : null;
  const clientData = useClientProfile(clientId, clientAuth);

  const { data: portfolioPlan } = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );

  return (
    <BorderedRow
      left={
        clientData.isLoading ? (
          <Skeleton width={120} />
        ) : clientData.hasError ? (
          UNKNOWN_USER
        ) : (
          `${clientData.data.investorProfile.personalDetails.name.first} ${clientData.data.investorProfile.personalDetails.name.last}`
        )
      }
      middle={portfolioPlan?.data.plan.name}
      right={immediateWeight?.mul(100).toFixed(2).toString() + "%"}
      removeBorderBottom={lastItem}
    />
  );
};

type PortfolioRowProps = {
  portfolioPlanId: PortfolioPlanId;
  effectiveWeight: Decimal | null;
  lastRow?: boolean;
};

const PortfolioRow: React.FC<PortfolioRowProps> = ({
  portfolioPlanId,
  effectiveWeight,
  lastRow,
}) => {
  const auth = useAdvisorRequestAuth();
  const { data: portfolioPlan } = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );

  return (
    <BorderedRow
      left={portfolioPlan?.data.plan.name}
      right={effectiveWeight?.mul(100).toFixed(2).toString() + "%"}
      removeBorderBottom={lastRow}
    />
  );
};

const LoadingState = ({ title }: { title: string }) => {
  return (
    <TableContainerV2>
      <TableHeaderV2 leftTitle={title} rightTitle={"Loading"} />
      <BorderedRow
        left={<Skeleton width={120} />}
        right={<Skeleton width={120} />}
      />
      <BorderedRow
        left={<Skeleton width={120} />}
        right={<Skeleton width={120} />}
      />
    </TableContainerV2>
  );
};

const ErrorState = ({ title }: { title: string }) => {
  return (
    <TableContainerV2>
      <TableHeaderV2 leftTitle={title} rightTitle={""} />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{
          height: "200px",
          justifyContent: "center",
        }}
      >
        <ErrorIcon color={"info"} fontSize="small" />
        <Text variant="body" color="textSubtle">
          Error loading table
        </Text>
      </Stack>
    </TableContainerV2>
  );
};

const EmptyState = ({ title }: { title: string }) => {
  return (
    <TableContainerV2>
      <TableHeaderV2 leftTitle={title} rightTitle={""} />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{
          height: "200px",
          justifyContent: "center",
        }}
      >
        <Text variant="body" color="textSubtle">
          No data to show
        </Text>
      </Stack>
    </TableContainerV2>
  );
};
